import React, { useEffect, useState } from "react";
import { Grid } from "antd";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";

import { usePopularChartContext } from "../../contexts/popularchartcontext";
import { ZERO_REFERENCE_LINE } from "../constants";
import { OPTIONS_BY_CHART, TREND_SETTINGS, YAXIS_SETTINGS } from "./constants";
import { EMPTY_ALERT_MODAL_DATA, ENTITY_TYPES } from "../../../utils/constants";
import RangePicker from "../RangePicker";
import FullscreenControl from "../FullscreenControl";
import ChartSettings from "./ChartSettings";
import PopularCardTooltip from "./PopularCardTooltip";
import AddAlertModal from "../../modals/AddAlertModal";
import PopularChartLegend from "./PopularChartLegend";

const lineFilter = (isTrend) => ({
  [TREND_SETTINGS.LINE_AND_TREND]: true,
  [TREND_SETTINGS.LINE_ONLY]: !isTrend,
  [TREND_SETTINGS.TREND_ONLY]: isTrend,
});

const filterLines = (lines, trendSetting) => {
  return lines.filter((line) => {
    return lineFilter(!!line.is_trend)[trendSetting];
  });
};

const Y_DOMAINS = {
  [YAXIS_SETTINGS.ZERO]: [0, "auto"],
  [YAXIS_SETTINGS.LOW_SALE]: ["dataMin", 0],
};

const PopularCardChart = ({ initialSearchPerformed, entityType = ENTITY_TYPES.CARD }) => {
  const [fullscreen, setFullscreen] = useState(false);
  const [filteredLines, setFilteredLines] = useState([]);
  const [yDomain, setYDomain] = useState();
  const [alertModalData, setAlertModalData] = useState(EMPTY_ALERT_MODAL_DATA);

  const {
    timespanPoints,
    lineProps,
    trendSetting,
    yAxisSetting,
    timespanSetting,
    calculateDataByTimespan,
    setEntityActiveAlert,
    setHoveringEntityId,
    hiddenLines,
    selectedGraph,
    pointsByDay,
    cards,
    sealedWaxes,
    setTimespanPoints,
  } = usePopularChartContext();
  const { lg } = Grid.useBreakpoint();

  const [entities, alertModalIdentifier] = {
    [ENTITY_TYPES.CARD]: [cards, "cardId"],
    [ENTITY_TYPES.SEALED_WAX]: [sealedWaxes, "sealedWaxId"],
  }[entityType];

  useEffect(() => {
    setTimespanPoints({
      ...timespanPoints,
      [timespanSetting]: calculateDataByTimespan(timespanSetting),
    });
  }, [timespanSetting]);

  useEffect(() => {
    const filteredLines = filterLines(lineProps, trendSetting);
    setFilteredLines(filteredLines);
  }, [lineProps, hiddenLines, trendSetting, selectedGraph]);

  useEffect(() => {
    setYDomain(Y_DOMAINS[yAxisSetting]);
  }, [yAxisSetting]);

  const chartLegendHoverFns = {
    onEnter: (el) => {
      let target = el.target.closest("li");
      if (!target) return;

      const { dataset } = target;
      const [entityId] = dataset.key.split("_");

      if (hiddenLines[entityId]) {
        setHoveringEntityId(null);
        return;
      }

      setHoveringEntityId(entityId);
    },
    onLeave: () => {
      setHoveringEntityId(null);
    },
  };

  const lines = filteredLines.map((line) => <Line {...line} key={line.key} />);

  if (!initialSearchPerformed) {
    return (
      <div className="saved-well">
        <p style={{ textAlign: "left", fontSize: 16, marginBottom: 0, fontWeight: "bold" }}>
          Use the search on the left to get started.
        </p>
      </div>
    );
  }

  const calcChartHeight = (lines) => {
    if (lines.length > 16) return 700;
    return 500;
  };

  const calcLegendHeight = (lines) => {
    if (lines.length > 16) return 264;
    return 132;
  };

  const renderLegend = () => {
    if (!entities?.length) {
      return null;
    }

    return (
      <Legend
        content={({ payload }) => (
          <PopularChartLegend
            entityType={entityType}
            payload={payload}
            isLiveSearch={false}
            setAlertModalData={setAlertModalData}
            hoverFns={chartLegendHoverFns}
          />
        )}
        height={calcLegendHeight(lineProps)}
        layout="horizontal"
        verticalAlign="bottom"
      />
    );
  };

  return (
    <div
      className={fullscreen ? "chart-fullscreen" : "chart-notfullscreen"}
      style={{ height: calcChartHeight(lineProps) }}
    >
      <AddAlertModal
        cardId={alertModalData.cardId}
        sealedWaxId={alertModalData.sealedWaxId}
        visible={alertModalData.visible}
        defaultData={{ name: alertModalData.name }}
        onCancel={() => setAlertModalData(EMPTY_ALERT_MODAL_DATA)}
        onSubmit={() => {
          setEntityActiveAlert(alertModalData[alertModalIdentifier]);
          // TODO: PCC Check this undefined stuff
          // setHasAlert(alertModalData.cardId);
        }}
      />
      <div>
        <RangePicker displayQuickOptions={lg} />
        {pointsByDay.length === 0 ? (
          <b
            style={{
              position: "absolute",
              top: "30%",
              left: lg ? "40%" : "20%",
              textAlign: "center",
            }}
          >
            No Results - Please Try Expanding Your Date Range
          </b>
        ) : null}
      </div>

      {pointsByDay.length !== 0 ? (
        <>
          <FullscreenControl
            isFullscreen={fullscreen}
            enterAction={() => setFullscreen(true)}
            exitAction={() => setFullscreen(false)}
          />
          <ChartSettings lineLabel={"Sales"} enableTrend />
          <div style={{ width: "100%", height: "98%", minWidth: 300, minHeight: 100, position: "relative" }}>
            <ResponsiveContainer height="98%" width="100%">
              <LineChart data={timespanPoints[timespanSetting]} margin={{ top: 5, right: 20, bottom: 5, left: 5 }}>
                <CartesianGrid stroke="#ccc" />
                {lines}
                {selectedGraph === "percentageGrowthChart" && (
                  <Line
                    strokeWidth={4}
                    dataKey={ZERO_REFERENCE_LINE}
                    name={ZERO_REFERENCE_LINE}
                    key={ZERO_REFERENCE_LINE}
                    stroke={"#333"}
                    dot={false}
                  />
                )}
                <XAxis dataKey={"name"} />
                <YAxis tickFormatter={(val) => OPTIONS_BY_CHART[selectedGraph].yTickFormatter(val)} domain={yDomain} />
                <Tooltip content={<PopularCardTooltip />} />
                {renderLegend()}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      ) : null}
    </div>
  );
};

export default PopularCardChart;
